/**
 * useShellWriters - the three writers every other shell hook is handed.
 *
 * - `updateTab(tabId, updater)`: every write to a combatant, with the GROUND
 *   taken back out of it. The updater is handed the RAW tab, but several
 *   writers build their condition list out of the DERIVED target (ground
 *   grants included) and merge it into the raw one - attacking or ending a
 *   turn in a mire used to write the mire onto the combatant for good
 *   (v7.77.0), and the ground's traits likewise (v8.310.9). Guarded HERE,
 *   at the one point where a tab becomes stored state, rather than at each
 *   writer - the next writer would forget too. The legacy Tab round-trips
 *   through the roster adapter, which splits the Target back into state and
 *   overrides, so call sites need no classification.
 * - `addNarrative(tabId, entry)`: a line in that combatant's log, stamped
 *   with their name, and shared.
 * - `addRollNarrative(entry)`: an attacker-side line (tests, dice tray) in
 *   the active tab's log WITHOUT the name, and shared.
 *
 * Also points `addNarrativeRef` at the current `addNarrative`, for the
 * callbacks declared above it. Extracted from DeathWatchSimulator.
 */
import { noteRosterWrite } from '../diagnostics/writeTrace';
import { useCallback } from 'react';
import { applyLegacyTab, toLegacyTab } from '../engine/roster/legacyAdapter';
import { withoutDerivedConditions, withoutDerivedTraits } from '../engine/roster/targetSplit';
import { diag } from '../diagnostics/log';
import useStableCallback from './useStableCallback';

/** The updated tab with whatever the ground granted subtracted back out. */
export function withoutGround(updated, before, ground) {
  const derived = ground?.grants;
  const target = withoutDerivedTraits(
    derived?.length ? withoutDerivedConditions(updated.target, derived) : updated.target,
    before.target,
    ground?.traits,
  );
  return target === updated.target ? updated : { ...updated, target };
}

/** The guarded tab writer; the ground is read through the ref, at write time. */
function useUpdateTab(setRoster, syncTargetToFirestore, surfaceEffectsRef) {
  // STABLE (plan shell-contexts, A3): `syncTargetToFirestore` is new on most renders, and every handler and context
  // built over this one took a new identity with it. A write, never read while drawing.
  return useStableCallback((tabId, updater) => {
    // Which combatant, for the sandbox's write trace (a no-op elsewhere).
    noteRosterWrite(tabId, 'tab');
    setRoster((prev) =>
      prev.map((c) => {
        if (c.id !== tabId) return c;
        const before = toLegacyTab(c);
        const guarded = withoutGround(updater(before), before, surfaceEffectsRef.current[tabId]);
        syncTargetToFirestore(tabId, guarded.target);
        return applyLegacyTab(c, guarded);
      }),
    );
  });
}

/**
 * @param {object} args - roster, setRoster, activeTab, appendNarrative,
 *   syncTargetToFirestore, syncNarrativeToFirestore, surfaceEffectsRef,
 *   addNarrativeRef.
 */
export default function useShellWriters(args) {
  const { roster, setRoster, activeTab, appendNarrative, syncTargetToFirestore, syncNarrativeToFirestore } = args;
  const updateTab = useUpdateTab(setRoster, syncTargetToFirestore, args.surfaceEffectsRef);
  const addNarrative = useCallback(
    (tabId, entry) => {
      const tName = roster.find((c) => c.id === tabId)?.name || '';
      const enriched = tName ? { ...entry, targetName: tName } : entry;
      appendNarrative(tabId, enriched);
      if (enriched.text) diag.info('log', String(enriched.text).slice(0, 160));
      // Sync outside the state updater to prevent double-invocation in StrictMode.
      syncNarrativeToFirestore(enriched);
    },
    [roster, appendNarrative, syncNarrativeToFirestore],
  );
  args.addNarrativeRef.current = addNarrative;
  const addRollNarrative = useCallback(
    (entry) => {
      if (activeTab) appendNarrative(activeTab, entry);
      syncNarrativeToFirestore(entry);
    },
    [activeTab, appendNarrative, syncNarrativeToFirestore],
  );
  return { updateTab, addNarrative, addRollNarrative };
}
